import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { WebhookDeliveryStatus, WebhookFormValues, WebhookSubscription, WebhookSubscriptionRequest } from "./types";

dayjs.extend(utc);

const EVENT_TYPE_LABELS: Record<string, string> = {
  "product.created": "Produkt skapad",
  "product.updated": "Produkt uppdaterad",
  "product.deleted": "Produkt raderad",
  "webhook.test": "Test",
};

const STATUS_LABELS: Record<string, string> = {
  Pending: "Väntar",
  Succeeded: "Levererad",
  Failed: "Misslyckad",
};

const STATUS_COLORS: Record<string, string> = {
  Pending: "processing",
  Succeeded: "success",
  Failed: "error",
};

const MANUAL_DISABLE_REASON = "Disabled manually.";

export function eventTypeLabel(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType] ?? eventType;
}

export function deliveryStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function deliveryStatusColor(status: string): string {
  return STATUS_COLORS[status] ?? "default";
}

export function isManuallyDisabled(subscription: Pick<WebhookSubscription, "isActive" | "disabledReason">): boolean {
  return !subscription.isActive && subscription.disabledReason === MANUAL_DISABLE_REASON;
}

export function subscriptionStatus(subscription: Pick<WebhookSubscription, "isActive" | "disabledReason">): { label: string; color: string } {
  if (subscription.isActive) {
    return { label: "Aktiv", color: "success" };
  }
  if (isManuallyDisabled(subscription)) {
    return { label: "Avstängd", color: "default" };
  }
  return { label: "Avstängd automatiskt", color: "error" };
}

export function formatDateTime(value?: string | null): string {
  if (!value) {
    return "–";
  }
  const hasZone = /(?:Z|[+-]\d{2}:\d{2})$/.test(value);
  const parsed = hasZone ? dayjs(value) : dayjs.utc(value);
  if (!parsed.isValid()) {
    return "–";
  }
  return parsed.local().format("YYYY-MM-DD HH:mm");
}

export function formatPayload(payload?: string | null): string {
  if (!payload) {
    return "";
  }
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
}

export function toSubscriptionRequest(values: WebhookFormValues): WebhookSubscriptionRequest {
  const header = values.apiKeyHeaderName?.trim();
  return {
    name: values.name.trim(),
    url: values.url.trim(),
    apiKeyHeaderName: header ? header : null,
    eventTypes: values.eventTypes ?? [],
  };
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === "string") {
    const trimmed = error.trim();
    if (!trimmed || trimmed.startsWith("Request failed with status code")) {
      return fallback;
    }
    return trimmed;
  }

  if (error instanceof Error) {
    return apiErrorMessage(error.message, fallback);
  }

  if (!error || typeof error !== "object") {
    return fallback;
  }

  const body = error as Record<string, unknown>;
  for (const key of ["detail", "Detail", "message", "Message", "error", "Error", "title", "Title"]) {
    const value = body[key];
    if (typeof value === "string" && value.trim() && !isGenericHttpTitle(value)) {
      return value.trim();
    }
  }

  const errors = body.errors ?? body.Errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors as Record<string, unknown>).flat()[0];
    if (typeof first === "string" && first.trim()) {
      return first.trim();
    }
  }

  return fallback;
}

function isGenericHttpTitle(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized === "bad request" || normalized === "one or more validation errors occurred.";
}

export const DELIVERY_STATUS_OPTIONS: { value: WebhookDeliveryStatus; label: string }[] = [
  { value: "Pending", label: "Väntar" },
  { value: "Succeeded", label: "Levererad" },
  { value: "Failed", label: "Misslyckad" },
];
